import {
  HttpContextToken,
  HttpResponse,
  type HttpInterceptorFn,
} from '@angular/common/http';
import { inject, isDevMode, PLATFORM_ID } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import {
  catchError,
  concat,
  concatMap,
  EMPTY,
  from,
  map,
  of,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';
import { CacheService } from './cache.service';

/** Context to set the cache key.
 *
 * For example, you can set the cache key to 'services' to cache all GET services requests.
 *
 * Must be used in tandem with {@link UNCACHE_TOKEN} for cache invalidation
 */
export const CACHE_TOKEN = new HttpContextToken(() => '');

/** Context to unset the cache key.
 *
 * For example, you can set the cache key to 'services' to unset all GET services requests.
 *
 * Must be used in tandem with {@link CACHE_TOKEN} for cache invalidation
 */
export const UNCACHE_TOKEN = new HttpContextToken(() => [] as string[]);

/** Interceptor to cache GET requests using the browser Cache API.
 *
 * Stale-while-revalidate: serves cached response immediately,
 * then fires the network request in the background to update the cache.
 *
 * CACHING IGNORED IF:
 *  -  client or server explicitly sets Cache-Control to no-cache or no-store
 *  -  is running in ssr context
 *
 * IMPORTANT!
 *  - MUST be the last interceptor in the chain.
 *
 */
export const cacheInterceptor: HttpInterceptorFn = (request, next) => {
  const platform = inject(PLATFORM_ID);
  const cache = inject(CacheService);

  const cacheControl = request.headers.get('Cache-Control');
  if (
    cacheControl?.includes('no-cache') ||
    cacheControl?.includes('no-store') ||
    isPlatformServer(platform)
  ) {
    return next(request);
  }
  const uncache = request.context.get(UNCACHE_TOKEN);
  const cacheKey = request.context.get(CACHE_TOKEN);

  const buildKey = (prefix: string) => {
    return `${prefix}:::${request.urlWithParams}`;
  };

  if (cacheKey && request.method === 'GET') {
    const key = buildKey(cacheKey);
    // Snapshot the invalidation epoch when the GET starts. A concurrent
    // mutation that invalidates `cacheKey` bumps this epoch, so a late
    // revalidation write (below) is dropped instead of resurrecting the key.
    const epoch = cache.invalidationEpoch(cacheKey);

    const networkRequest$ = next(request).pipe(
      tap((event) => {
        if (
          event instanceof HttpResponse &&
          event.ok &&
          request.responseType === 'json' &&
          !event.headers.get('Cache-Control')?.includes('no-cache') &&
          !event.headers.get('Cache-Control')?.includes('no-store') &&
          !request.reportProgress
        ) {
          cache.set(key, event, cacheKey, epoch);
        }
      }),
    );

    return from(cache.get(key)).pipe(
      switchMap((cached) => {
        // Cold miss — normal network request, errors propagate.
        if (!cached) return networkRequest$;

        // Stale-while-revalidate. CONTRACT: a cache hit is a MULTI-EMIT stream
        // — cached value first (fast paint), then the revalidated network
        // value — so rxResource-backed views update stale -> fresh. Every
        // cached-GET consumer must tolerate two emissions:
        //  - rxResource / streams: receive both (the point of #040).
        //  - firstValueFrom: resolves with the cached value (one-and-done).
        //  - bare .subscribe: runs once per emission — handlers must be
        //    idempotent (all current ones set a signal / patch store state).
        //  - lastValueFrom: resolves with the fresh value after revalidation.
        // `revalidate$` is shared and subscribed eagerly so the background
        // network write still happens (and the cache self-heals) even when a
        // consumer unsubscribes after the cached value — without it,
        // firstValueFrom would cancel the revalidation and leave a stale key.
        const revalidate$ = networkRequest$.pipe(
          catchError((err) => {
            // A failed revalidation must not error the subscriber after we
            // delivered good cached data — keep the cached value standing.
            if (isDevMode())
              console.warn(
                '[CacheInterceptor] Background revalidation failed',
                err,
              );
            return EMPTY;
          }),
          shareReplay({ bufferSize: 1, refCount: false }),
        );
        revalidate$.subscribe();
        return concat(of(cached), revalidate$);
      }),
    );
  }

  return next(request).pipe(
    concatMap((event) => {
      if (
        event instanceof HttpResponse &&
        event.ok &&
        request.method !== 'GET' &&
        uncache.length > 0
      ) {
        return from(cache.deleteByPrefix(uncache)).pipe(map(() => event));
      }
      return of(event);
    }),
  );
};
